'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { ChevronLeft, CloseIcon } from '@/components/icons';
import './mobileNavPanel.css';

const ANIMATION_MS = 250;
/** Minimum downward drag (px) before a swipe dismisses the panel. */
const SWIPE_CLOSE_THRESHOLD = 90;

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

type MobileNavPanelProps = {
  open: boolean;
  title: string;
  onClose: () => void;
  /** When set, the header arrow steps back to the previous view instead of closing the panel. */
  onBack?: () => void;
  children: React.ReactNode;
};

/**
 * Full-screen, mobile-only navigation panel.
 *
 * Behaviour: slide-up entrance/exit (~250ms), body scroll lock, focus trap + restore,
 * Escape / backdrop / close-button dismissal (the back arrow dismisses too, unless `onBack`
 * makes it step back a view instead), swipe-down-to-dismiss, and
 * browser-back integration (opening pushes a history entry so the hardware/browser back
 * button closes the panel instead of leaving the page).
 */
export function MobileNavPanel({ open, title, onClose, onBack, children }: MobileNavPanelProps) {
  const [mounted, setMounted] = useState(false);
  const [entered, setEntered] = useState(false);

  const overlayRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  /** True while a history entry we pushed is still on the stack. */
  const pushedHistory = useRef(false);
  /** Drag state for swipe-to-dismiss. */
  const dragStartY = useRef<number | null>(null);
  const dragOffset = useRef(0);

  // Mount/unmount around the slide animation.
  useEffect(() => {
    if (open) setMounted(true);
  }, [open]);

  useEffect(() => {
    if (!mounted) return;
    if (open) {
      const id = requestAnimationFrame(() => setEntered(true));
      return () => cancelAnimationFrame(id);
    }
    setEntered(false);
    const timer = setTimeout(() => setMounted(false), ANIMATION_MS);
    return () => clearTimeout(timer);
  }, [open, mounted]);

  // Lock background scroll while the panel occupies the DOM.
  useEffect(() => {
    if (!mounted) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [mounted]);

  // Focus management: capture the outside focus for the panel's whole lifetime, restore on close.
  useEffect(() => {
    if (!open) return;
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    return () => {
      previouslyFocused.current?.focus?.();
    };
  }, [open]);

  // Move focus to the panel header once it is in the DOM, and again whenever the view changes,
  // so drilling into a sub-view never drops focus onto the unmounted trigger (escaping the trap).
  useEffect(() => {
    if (!open || !mounted) return;
    panelRef.current?.querySelector<HTMLElement>('[data-autofocus]')?.focus();
  }, [open, mounted, title]);

  // Browser/hardware back button closes the panel (instead of navigating away).
  useEffect(() => {
    if (!open) return;
    window.history.pushState({ __faunaMobileNav: true }, '');
    pushedHistory.current = true;
    const onPopState = () => {
      pushedHistory.current = false;
      onCloseRef.current();
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [open]);

  /** Single close path — unwinds the pushed history entry so the stack stays clean. */
  const requestClose = useCallback(() => {
    if (pushedHistory.current) {
      pushedHistory.current = false;
      window.history.back();
      return;
    }
    onCloseRef.current();
  }, []);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        requestClose();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusables = Array.from(
        panelRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR) ?? []
      ).filter((el) => el.offsetParent !== null);
      if (focusables.length === 0) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const activeEl = document.activeElement as HTMLElement | null;

      if (event.shiftKey && activeEl === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && activeEl === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [requestClose]
  );

  // Swipe-down-to-dismiss, initiated from the header handle so it never fights content scroll.
  const onTouchStart = (event: React.TouchEvent) => {
    dragStartY.current = event.touches[0].clientY;
    dragOffset.current = 0;
  };

  const onTouchMove = (event: React.TouchEvent) => {
    if (dragStartY.current == null) return;
    const delta = event.touches[0].clientY - dragStartY.current;
    if (delta <= 0) {
      dragOffset.current = 0;
      if (panelRef.current) panelRef.current.style.transform = '';
      return;
    }
    dragOffset.current = delta;
    if (panelRef.current) {
      panelRef.current.style.transition = 'none';
      panelRef.current.style.transform = `translateY(${delta}px)`;
    }
  };

  const onTouchEnd = () => {
    if (panelRef.current) {
      panelRef.current.style.transition = '';
      panelRef.current.style.transform = '';
    }
    const shouldClose = dragOffset.current > SWIPE_CLOSE_THRESHOLD;
    dragStartY.current = null;
    dragOffset.current = 0;
    if (shouldClose) requestClose();
  };

  if (!mounted) return null;

  return createPortal(
    <div
      ref={overlayRef}
      className="mobile-nav-overlay"
      data-open={entered ? 'true' : 'false'}
      onKeyDown={onKeyDown}
      onMouseDown={(e) => {
        if (e.target === overlayRef.current) requestClose();
      }}
      role="presentation"
    >
      <div
        ref={panelRef}
        className="mobile-nav-panel"
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div
          className="flex shrink-0 items-center justify-between gap-3 border-b border-gray-800 px-4 py-4"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <button
            type="button"
            data-autofocus
            onClick={onBack ?? requestClose}
            className="flex items-center gap-1 appearance-none border-0 bg-transparent p-0 font-inter text-lg font-semibold text-gray-100 transition-colors hover:text-primary-400"
            aria-label={onBack ? `Back from ${title}` : `Close ${title}`}
          >
            <ChevronLeft size={22} />
            <span>{title}</span>
          </button>
          <button
            type="button"
            onClick={requestClose}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-gray-800 bg-transparent transition-colors hover:bg-gray-800"
            aria-label="Close navigation menu"
          >
            <CloseIcon />
          </button>
        </div>

        <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4">
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
}
